import { useEffect } from "react";
import { useLocale } from "next-intl";
import {
  learningLocale,
  localizedLearningPath,
  unlocalizedLearningPath,
} from "@/lib/public-learning-routes";
import pages from "../../../config/content/public_learning.json";
import siteCopy from "../../../config/content/site.json";

// Match Rails metadata after in-app navigation as well as on a direct request.
export function usePublicLearningMetadata(pathname: string) {
  const preferredLocale = useLocale();
  useEffect(() => {
    const home = pathname === "/";
    const locale = home
      ? preferredLocale === "zh-CN"
        ? "zh-CN"
        : "en"
      : learningLocale(pathname);
    if (!locale) return;
    const page = home
      ? {
          path: "/",
          title: siteCopy.en.title,
          title_zh: siteCopy["zh-CN"].title,
          description: siteCopy.en.description,
          description_zh: siteCopy["zh-CN"].description,
        }
      : pages.find(
          (item) =>
            item.path === unlocalizedLearningPath(pathname).replace(/\/$/, ""),
        );
    if (!page) return;
    const undo: (() => void)[] = [];
    if (home) {
      document.head
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((element) => {
          element.remove();
          undo.push(() => document.head.append(element));
        });
    }
    function set(
      selector: string,
      tag: "meta" | "link",
      attributes: Record<string, string>,
    ) {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement(tag);
        document.head.append(element);
        const created = element;
        undo.push(() => created.remove());
      } else {
        const target = element;
        const old = Object.fromEntries(
          Object.keys(attributes).map((key) => [key, target.getAttribute(key)]),
        );
        undo.push(() =>
          Object.entries(old).forEach(([key, value]) =>
            value == null
              ? target.removeAttribute(key)
              : target.setAttribute(key, value),
          ),
        );
      }
      Object.entries(attributes).forEach(([key, value]) =>
        element.setAttribute(key, value),
      );
    }
    const pageTitle = locale === "en" ? page.title : page.title_zh;
    const title = home ? pageTitle : `${pageTitle} | ABG One`;
    const description =
      locale === "en" ? page.description : page.description_zh;
    const origin = new URL(
      document.head
        .querySelector('link[rel="canonical"]')
        ?.getAttribute("href") ?? window.location.origin,
      window.location.origin,
    ).origin;
    const canonical = `${origin}${home ? "/" : localizedLearningPath(page.path, locale)}`;
    document.title = title;
    set('link[rel="canonical"]', "link", { rel: "canonical", href: canonical });
    for (const [name, content] of Object.entries({
      description,
      robots: "index, follow",
      "twitter:title": title,
      "twitter:description": description,
    }))
      set(`meta[name="${name}"]`, "meta", { name, content });
    for (const [property, content] of Object.entries({
      "og:title": title,
      "og:description": description,
      "og:url": canonical,
      "og:locale": locale === "en" ? "en_US" : "zh_CN",
    }))
      set(`meta[property="${property}"]`, "meta", { property, content });
    for (const language of home ? [] : (["en", "zh-CN", "x-default"] as const))
      set(`link[hreflang="${language}"]`, "link", {
        rel: "alternate",
        hreflang: language,
        href: `${origin}${localizedLearningPath(page.path, language === "zh-CN" ? "zh-CN" : "en")}`,
      });
    return () => undo.reverse().forEach((restore) => restore());
  }, [pathname, preferredLocale]);
}
